import { Container, Sprite, Texture } from "pixi.js"

/**
 * What comes off a tree: bark chips, leaves and puffs of dust. Shared by the blows that work a tree
 * (TreeHit) and the fall that ends it (TreeFall), so the two draw from one pool under one cap.
 *
 * Every bit lives in ground coordinates -- a ground position and a height above it, drawn at
 * (x, groundY - z) and sorted on groundY -- so a chip that flies up does not change which bodies it
 * sorts behind. The bits are ordinary z-sorted children of the world surface, so they interleave with
 * bodies the way FootDust's puffs do.
 *
 * NO TIMERS. The owner calls update() once per drawn frame.
 */

export interface TreeDebrisTextures {
    leaves: Texture[]
    puffs: Texture[]
}

const enum Kind {
    Chip,
    Leaf,
    Puff,
}

interface Bit {
    kind: Kind
    sprite: Sprite
    x: number
    groundY: number
    z: number
    vx: number
    vy: number
    vz: number
    // Never sort behind the tree it came off: leaves fall down its FRONT.
    zFloor: number
    spin: number
    scale: number
    alpha: number
    bornMs: number
    // 0 while airborne.
    landedMs: number
    restMs: number
    bounces: number
    // Leaf flight.
    terminal: number
    swayAmp: number
    swayFreq: number
    swayPhase: number
    flutterFreq: number
    flutterPhase: number
    // Puff life.
    lifeMs: number
    growth: number
}

/** Where a bit starts and how it is moving: ground position, height, and velocity in px/s. */
export interface DebrisLaunch {
    x: number
    groundY: number
    z: number
    vx: number
    vy?: number
    vz: number
    zFloor: number
}

// Same convention as FootDust: something lying on the ground at groundY sorts like a body whose soles
// are there, and a body's sort line is its hip, this far above its soles.
const FOOT_DROP = 20

const CHIP_GRAVITY = 480
const CHIP_REST_MS = [450, 950]
const LEAF_GRAVITY = 160
const LEAF_DRAG = 2.4
const LEAF_REST_MS = [600, 1300]
const FADE_MS = 380

// Total live particles. A crowd of woodcutters is the case this is for -- the particles are garnish,
// and dropping the newest is invisible where a frame spent on hundreds of sprites is not.
export const MAX_DEBRIS = 140

export const between = (min: number, max: number) => min + Math.random() * (max - min)
export const pick = <T>(list: T[]): T => list[Math.floor(Math.random() * list.length)]

export class TreeDebris {
    private surface: Container
    private makeTextures: () => TreeDebrisTextures
    private textures: TreeDebrisTextures | undefined
    private bits: Bit[] = []
    private pool: Sprite[] = []

    constructor(surface: Container, makeTextures: () => TreeDebrisTextures) {
        this.surface = surface
        this.makeTextures = makeTextures
    }

    setSurface(surface: Container): void {
        this.surface = surface
    }

    count = () => this.bits.length

    /** The leaf and puff textures, built on first use: in a test there is no GPU to bake them. */
    get leaves(): Texture[] {
        return this.ensureTextures().leaves
    }

    get puffs(): Texture[] {
        return this.ensureTextures().puffs
    }

    /** A chip of wood or bark: arcs out, bounces once, lies in the dirt. Size in px. */
    chip = (at: DebrisLaunch, tint: number, nowMs: number, size: { w: number; h: number } = { w: between(1.5, 3), h: between(1, 1.8) }) => {
        const sprite = this.add(Texture.WHITE)
        if (!sprite) return false
        sprite.anchor.set(0.5)
        sprite.scale.set(size.w / Texture.WHITE.width, size.h / Texture.WHITE.height)
        sprite.rotation = Math.random() * Math.PI
        sprite.tint = tint
        this.push({
            ...this.blank(Kind.Chip, sprite, nowMs, at),
            spin: (Math.random() < 0.5 ? -1 : 1) * between(8, 20),
            restMs: between(CHIP_REST_MS[0], CHIP_REST_MS[1]),
        })
        return true
    }

    /** A leaf: flung, then the air takes over -- it sways, tumbles edge-on and settles flat. */
    leaf = (at: DebrisLaunch, tint: number, nowMs: number, scale = between(0.26, 0.4)) => {
        const leaves = this.leaves
        if (!leaves.length) return false
        const sprite = this.add(pick(leaves))
        if (!sprite) return false
        sprite.anchor.set(0.5)
        sprite.scale.set(scale)
        sprite.rotation = Math.random() * Math.PI * 2
        sprite.tint = tint
        this.push({
            ...this.blank(Kind.Leaf, sprite, nowMs, at),
            scale,
            terminal: between(40, 62),
            swayAmp: between(10, 22),
            swayFreq: between(2.2, 4),
            swayPhase: Math.random() * Math.PI * 2,
            flutterFreq: between(5, 10),
            flutterPhase: Math.random() * Math.PI * 2,
            spin: (Math.random() < 0.5 ? -1 : 1) * between(1.5, 4.5),
            restMs: between(LEAF_REST_MS[0], LEAF_REST_MS[1]),
        })
        return true
    }

    /** A puff of dust that swells and thins out over `lifeMs`, `growth` times its starting size. */
    puff = (at: DebrisLaunch, tint: number, nowMs: number, look: { scale: number; alpha: number; lifeMs: number; growth: number }) => {
        const puffs = this.puffs
        if (!puffs.length) return false
        const sprite = this.add(pick(puffs))
        if (!sprite) return false
        sprite.anchor.set(0.5, 0.7)
        sprite.tint = tint
        sprite.rotation = Math.random() * Math.PI * 2
        this.push({
            ...this.blank(Kind.Puff, sprite, nowMs, at),
            ...look,
            spin: between(-0.8, 0.8),
        })
        return true
    }

    update = (nowMs: number, dt: number) => {
        if (!this.bits.length) return
        const leafDrag = Math.exp(-LEAF_DRAG * dt)
        let write = 0
        for (let i = 0; i < this.bits.length; i++) {
            const bit = this.bits[i]
            const sprite = bit.sprite
            if (sprite.destroyed) continue

            if (bit.kind === Kind.Puff) {
                const age = (nowMs - bit.bornMs) / bit.lifeMs
                if (age >= 1) {
                    this.retire(bit)
                    continue
                }
                bit.x += bit.vx * dt
                bit.groundY += bit.vy * dt
                bit.z += bit.vz * dt
                bit.vx *= Math.exp(-5 * dt)
                bit.vy *= Math.exp(-5 * dt)
                bit.vz *= Math.exp(-3 * dt)
                sprite.rotation += bit.spin * dt
                const eased = 1 - (1 - age) * (1 - age)
                sprite.scale.set(bit.scale * (1 + (bit.growth - 1) * eased))
                sprite.alpha = bit.alpha * (age < 0.15 ? age / 0.15 : 1 - ((age - 0.15) / 0.85) ** 2)
            } else if (!bit.landedMs) {
                if (bit.kind === Kind.Chip) {
                    bit.vz -= CHIP_GRAVITY * dt
                    bit.x += bit.vx * dt
                    bit.groundY += bit.vy * dt
                    bit.z += bit.vz * dt
                    sprite.rotation += bit.spin * dt
                    if (bit.z <= 0) {
                        bit.z = 0
                        if (bit.bounces < 1 && bit.vz < -40) {
                            // One hop, much smaller, and most of the slide is gone.
                            bit.vz = -bit.vz * 0.32
                            bit.vx *= 0.45
                            bit.vy *= 0.45
                            bit.spin *= 0.5
                            bit.bounces++
                        } else {
                            bit.landedMs = nowMs
                        }
                    }
                } else {
                    bit.vz = Math.max(bit.vz - LEAF_GRAVITY * dt, -bit.terminal)
                    bit.vx *= leafDrag
                    bit.vy *= leafDrag
                    const t = (nowMs - bit.bornMs) / 1000
                    bit.x += (bit.vx + bit.swayAmp * Math.sin(t * bit.swayFreq + bit.swayPhase)) * dt
                    bit.groundY += bit.vy * dt
                    bit.z += bit.vz * dt
                    sprite.rotation += bit.spin * dt
                    // Turning edge-on and back as it tumbles.
                    const flutter = Math.abs(Math.cos(t * bit.flutterFreq + bit.flutterPhase))
                    sprite.scale.set(bit.scale, bit.scale * (0.3 + 0.7 * flutter))
                    if (bit.z <= 0) {
                        bit.z = 0
                        bit.landedMs = nowMs
                        // Lying flat, seen at the camera's angle.
                        sprite.scale.set(bit.scale, bit.scale * 0.55)
                    }
                }
            } else {
                const lying = nowMs - bit.landedMs
                if (lying >= bit.restMs + FADE_MS) {
                    this.retire(bit)
                    continue
                }
                sprite.alpha = lying > bit.restMs ? bit.alpha * (1 - (lying - bit.restMs) / FADE_MS) : bit.alpha
            }

            this.draw(bit)
            this.bits[write++] = bit
        }
        this.bits.length = write
    }

    /** Sweep every bit away (a new room). */
    clear = () => {
        this.bits.forEach(this.retire)
        this.bits = []
    }

    private draw = (bit: Bit) => {
        const sprite = bit.sprite
        sprite.x = bit.x
        sprite.y = bit.groundY - bit.z
        const z = Math.max(100 + bit.groundY - FOOT_DROP, bit.zFloor)
        // zIndex marks the whole world surface for a re-sort; only touch it when it moved.
        if (sprite.zIndex !== z) sprite.zIndex = z
    }

    private add = (texture: Texture): Sprite | undefined => {
        if (this.bits.length >= MAX_DEBRIS) return undefined
        const sprite = this.pool.pop() || new Sprite(texture)
        sprite.texture = texture
        sprite.alpha = 1
        sprite.scale.set(1)
        sprite.skew.set(0)
        sprite.rotation = 0
        this.surface.addChild(sprite)
        return sprite
    }

    private push = (bit: Bit) => {
        this.bits.push(bit)
        this.draw(bit)
    }

    private blank = (kind: Kind, sprite: Sprite, nowMs: number, at: DebrisLaunch): Bit => ({
        kind,
        sprite,
        x: at.x,
        groundY: at.groundY,
        z: at.z,
        vx: at.vx,
        vy: at.vy ?? 0,
        vz: at.vz,
        zFloor: at.zFloor,
        spin: 0,
        scale: 1,
        alpha: 1,
        bornMs: nowMs,
        landedMs: 0,
        restMs: 0,
        bounces: 0,
        terminal: 0,
        swayAmp: 0,
        swayFreq: 0,
        swayPhase: 0,
        flutterFreq: 0,
        flutterPhase: 0,
        lifeMs: 0,
        growth: 1,
    })

    // Built on the first particle, not at construction: the renderer may not be able to bake a texture
    // yet, and in a test there is no GPU at all.
    private ensureTextures = (): TreeDebrisTextures => {
        if (!this.textures) {
            try {
                this.textures = this.makeTextures()
            } catch (e) {
                this.textures = { leaves: [], puffs: [] }
            }
        }
        return this.textures
    }

    private retire = (bit: Bit) => {
        bit.sprite.parent?.removeChild(bit.sprite)
        if (!bit.sprite.destroyed) {
            if (this.pool.length < MAX_DEBRIS) this.pool.push(bit.sprite)
            else bit.sprite.destroy()
        }
    }
}
